import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import {
  esEmailValido,
  obtenerEmailMaestro,
} from "../alta_proveedores/altaProveedores.correo";
import { getProveedorByCuit } from "./ApiServices";
import { formatCuit } from "./utils";

const helperTextCorreoOrdenPago =
  "Puede modificar el correo si necesita enviar la orden de pago a otra dirección.";

export default function ModalConfirmarOrdenPago({
  open,
  onClose,
  onConfirm,
  cuit,
  razonSocial = "",
  emailInicial = "",
  generando = false,
  modo = "generar",
  nroOp = "",
  cantidadProveedores = 1,
}) {
  const esReenvio = modo === "reenviar";
  const esAprobar = modo === "aprobar" || modo === "actualizar";
  const esMultiProveedor = !esReenvio && !esAprobar && cantidadProveedores > 1;
  const [email, setEmail] = useState("");
  const [errorEmail, setErrorEmail] = useState("");
  const [cargandoEmail, setCargandoEmail] = useState(false);

  useEffect(() => {
    if (!open) {
      setEmail("");
      setErrorEmail("");
      return;
    }

    if (esMultiProveedor) {
      setEmail("");
      return;
    }

    const inicial = String(emailInicial || "").trim();
    if (inicial) {
      setEmail(inicial);
      return;
    }

    if (!cuit) return;

    let cancelled = false;

    const cargarEmail = async () => {
      setCargandoEmail(true);
      try {
        const proveedor = await getProveedorByCuit(cuit);
        if (cancelled) return;
        setEmail(obtenerEmailMaestro(proveedor));
      } catch {
        if (!cancelled) setEmail("");
      } finally {
        if (!cancelled) setCargandoEmail(false);
      }
    };

    cargarEmail();

    return () => {
      cancelled = true;
    };
  }, [open, cuit, emailInicial, esMultiProveedor]);

  const handleConfirmar = () => {
    if (esMultiProveedor) {
      onConfirm?.("");
      return;
    }
    const emailTrim = String(email || "").trim();
    if (!esEmailValido(emailTrim)) {
      setErrorEmail("Ingrese un correo válido para enviar la orden de pago");
      return;
    }
    onConfirm?.(emailTrim);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {esReenvio
          ? "Reenviar correo"
          : esAprobar
            ? "Aprobar orden de pago"
            : "Confirmar orden de pago"}
      </DialogTitle>
      <DialogContent>
        {esMultiProveedor ? (
          <>
            <Typography variant="body2" color="text.secondary">
              Se generará un lote con{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                {cantidadProveedores} órdenes de pago
              </Box>{" "}
              (una por proveedor seleccionado), que quedarán pendientes de
              aprobación.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Al confirmar no se notificará a los proveedores. El lote quedará
              pendiente de aprobación.
            </Alert>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary">
              {esReenvio
                ? `Se reenviará la documentación${
                    nroOp ? ` de la orden ${nroOp}` : " de la orden de pago"
                  } al siguiente correo.`
                : esAprobar
                  ? "Se aprobará la orden (pasará a Generada, lista para conciliación) y se enviará la documentación al correo indicado."
                  : "Se enviará la documentación de la orden de pago al siguiente correo del proveedor."}
            </Typography>

            {cuit ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {razonSocial ? (
                  <>
                    <Box
                      component="span"
                      sx={{ fontWeight: 600, color: "text.primary" }}
                    >
                      {razonSocial}
                    </Box>
                    {" · "}
                  </>
                ) : null}
                CUIT: {formatCuit(cuit)}
              </Typography>
            ) : null}

            <Alert severity="info" sx={{ mt: 2 }}>
              {esReenvio
                ? "Puede modificar el correo antes de reenviar si desea enviar la documentación a otra dirección."
                : esAprobar
                  ? "Puede modificar el correo antes de aprobar si desea notificar a otra dirección."
                  : "Puede modificar el correo antes de confirmar si desea enviar la orden de pago a otra dirección."}
            </Alert>

            {cargandoEmail ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Buscando correo del proveedor...
                </Typography>
              </Box>
            ) : (
              <TextField
                fullWidth
                required
                type="email"
                label="Correo del proveedor"
                placeholder="proveedor@ejemplo.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorEmail("");
                }}
                disabled={generando}
                error={Boolean(errorEmail)}
                helperText={errorEmail || helperTextCorreoOrdenPago}
                sx={{ mt: 2 }}
              />
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={generando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirmar}
          disabled={
            generando ||
            (!esMultiProveedor && (cargandoEmail || !esEmailValido(email)))
          }
          startIcon={
            generando ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {generando
            ? esReenvio
              ? "Reenviando..."
              : esAprobar
                ? "Aprobando..."
                : "Generando..."
            : esReenvio
              ? "Reenviar correo"
              : esAprobar
                ? "Aprobar"
                : "Confirmar y generar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
